import { useState } from "react";
import {
  formatEuro,
  formatQty,
  MATERIAL_UNITS,
  type BuildingDto,
  type MaterialDto,
  type MaterialScope,
  type MaterialUnit,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { parseEuro } from "../../pages/Platform";
import { NO_PARTY, PartyPicker } from "../directory/PartyPicker";
import { errMsg, useMaterialsRefresh } from "./useMaterials";

const euroInput = (c: number | null | undefined) => (c == null ? "" : (c / 100).toFixed(2).replace(".", ","));
/** Aantal uit een invoerveld: 2, 1,5 of 0.25. */
export const parseQty = (v: string): number | null => {
  const n = Number(v.trim().replace(",", "."));
  return v.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) / 1000 : null;
};

/** Nieuw artikel of artikel bewerken. */
export function MaterialDialog({
  building: b,
  material: m,
  onClose,
  onSaved,
}: {
  building: BuildingDto;
  material?: MaterialDto;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const toast = useToast();
  const refresh = useMaterialsRefresh(b.id);
  const [name, setName] = useState(m?.name ?? "");
  const [articleNo, setArticleNo] = useState(m?.articleNo ?? "");
  const [unit, setUnit] = useState<MaterialUnit>(m?.unit ?? "stuk");
  const [price, setPrice] = useState(euroInput(m?.priceCents));
  const [supplier, setSupplier] = useState(m?.supplier?.id ?? "");
  const [notes, setNotes] = useState(m?.notes ?? "");
  const [scope, setScope] = useState<MaterialScope>(b.client.isUnassigned ? "building" : "client");
  const [busy, setBusy] = useState(false);
  const priceCents = price.trim() ? parseEuro(price) : null;
  const badPrice = price.trim() !== "" && priceCents === null;

  async function save() {
    setBusy(true);
    const body = {
      name,
      articleNo: articleNo || null,
      unit,
      priceCents,
      supplierCompanyId: supplier || null,
      notes: notes || null,
    };
    try {
      if (m) {
        await api(`/buildings/${b.id}/materials/${m.id}`, { method: "PATCH", body });
        toast("Artikel opgeslagen");
        onSaved?.(m.id);
      } else {
        const id = crypto.randomUUID();
        await api(`/buildings/${b.id}/materials`, { method: "POST", body: { id, scope, ...body } });
        toast("Artikel toegevoegd");
        onSaved?.(id);
      }
      await refresh();
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={m ? "Artikel bewerken" : "Nieuw artikel"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || !name.trim() || badPrice}
          >
            {m ? "Opslaan" : "Toevoegen"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Naam *</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="bijv. Filter F7 592×592" />
      </label>
      <div className="grid2">
        <label className="field">
          <span>Artikelnummer</span>
          <input value={articleNo} onChange={(e) => setArticleNo(e.target.value)} placeholder="van de leverancier" />
        </label>
        <label className="field">
          <span>Eenheid</span>
          <select value={unit} onChange={(e) => setUnit(e.target.value as MaterialUnit)}>
            {MATERIAL_UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid2">
        <label className="field">
          <span>Prijs per {unit} (excl. btw)</span>
          <input
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            placeholder="bedrag"
            aria-invalid={badPrice || undefined}
          />
          <span className="hint">{badPrice ? "Bedrag als 18,50" : "Telt mee in de MJOP bij onderhoud"}</span>
        </label>
        <PartyPicker
          id="mat-supplier"
          label="Leverancier"
          value={{ ...NO_PARTY, companyId: supplier || null }}
          onChange={(p) => setSupplier(p.companyId ?? "")}
          savedName={m?.supplier?.name}
          buildingId={b.id}
          emptyLabel="Geen"
          withPerson={false}
          internal={false}
        />
      </div>
      {!m && !b.client.isUnassigned && (
        <fieldset className="obresult">
          <legend className="label">Geldt voor</legend>
          <label className="obres">
            <input type="radio" name="mat-scope" checked={scope === "client"} onChange={() => setScope("client")} />
            <span>Alle gebouwen van {b.client.name}</span>
          </label>
          <label className="obres">
            <input type="radio" name="mat-scope" checked={scope === "building"} onChange={() => setScope("building")} />
            <span>Alleen {b.name}</span>
          </label>
        </fieldset>
      )}
      <label className="field">
        <span>Notities</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </label>
    </Modal>
  );
}

/** Ontvangen (aantal erbij) of tellen (nieuwe stand). */
export function MoveDialog({
  buildingId,
  material: m,
  kind,
  onClose,
}: {
  buildingId: string;
  material: MaterialDto;
  kind: "received" | "correction";
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useMaterialsRefresh(buildingId);
  const [qty, setQty] = useState(kind === "correction" ? formatQty(m.stock?.quantity ?? 0) : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const n = parseQty(qty);
  const ok = n !== null && (kind === "correction" || n > 0);

  async function save() {
    setBusy(true);
    try {
      await api(`/buildings/${buildingId}/materials/${m.id}/moves`, {
        method: "POST",
        body: { kind, quantity: n, note: note || null },
      });
      await refresh();
      toast(kind === "received" ? "Ontvangst geboekt" : "Voorraad bijgewerkt");
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={kind === "received" ? `Ontvangen: ${m.name}` : `Voorraad tellen: ${m.name}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !ok}>
            Opslaan
          </button>
        </>
      }
    >
      <p className="muted">
        Nu op voorraad: {formatQty(m.stock?.quantity ?? 0)} {m.unit}
      </p>
      <label className="field">
        <span>{kind === "received" ? `Aantal ontvangen (${m.unit})` : `Geteld aantal (${m.unit})`}</span>
        <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" />
      </label>
      <label className="field">
        <span>Opmerking</span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={kind === "received" ? "bijv. pakbon 12345" : "bijv. telling na inspectieronde"}
        />
      </label>
    </Modal>
  );
}

/** Minimum en plek van de voorraad in dit gebouw. */
export function StockDialog({
  buildingId,
  material: m,
  onClose,
}: {
  buildingId: string;
  material: MaterialDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useMaterialsRefresh(buildingId);
  const [min, setMin] = useState(m.stock?.minQuantity != null ? formatQty(m.stock.minQuantity) : "");
  const [location, setLocation] = useState(m.stock?.location ?? "");
  const [busy, setBusy] = useState(false);
  const minQty = min.trim() ? parseQty(min) : null;
  const bad = min.trim() !== "" && minQty === null;

  async function save() {
    setBusy(true);
    try {
      await api(`/buildings/${buildingId}/materials/${m.id}/stock`, {
        method: "PUT",
        body: { minQuantity: minQty, location: location || null },
      });
      await refresh();
      toast("Opgeslagen");
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Minimum en plek: ${m.name}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || bad}>
            Opslaan
          </button>
        </>
      }
    >
      <div className="grid2">
        <label className="field">
          <span>Minimum ({m.unit})</span>
          <input value={min} onChange={(e) => setMin(e.target.value)} inputMode="decimal" placeholder="geen" />
          <span className="hint">Daaronder staat het artikel bij Onder minimum</span>
        </label>
        <label className="field">
          <span>Plek in het gebouw</span>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="bijv. technische ruimte, kast 2"
          />
        </label>
      </div>
    </Modal>
  );
}

export const priceLabel = (m: Pick<MaterialDto, "priceCents" | "unit">) =>
  m.priceCents == null ? "–" : `${formatEuro(m.priceCents)} per ${m.unit}`;
